import { useEffect, useState, useRef } from 'react';
import { Grid, Typography, Paper, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setPrenotaNewStanza } from '../../../redux/reducers/prenotazioneNew-reducer';
import axios from 'axios';
import { GetAllElelementsWithPrenotazioniCount } from '../../../const/urlAction';
import { useTranslation } from 'react-i18next';
import { ProgressBarDisponibilitaLinear, ProgressBarDisponibilitaCircular } from '../../Disponibilita/componentsViewDisponibilita/ProgressBarDisponibilita';
import CalcPercenteProgressBar from '../../../functions/disponibilita/calcPercenteProgressBar';
import FormatMinutiToOre from '../../../functions/disponibilita/formatMinutiToOre';
import useMobileDetection from '../../../Hooks/useMobileDetection';
import InlineLoader from '../../UI/spinner/InlineLoader';
import moment from 'moment';
import '../../Disponibilita/panoramica.css';

const urlBase = process.env.REACT_APP_URL;
const defaultPercent = { "lowStatus": 25, "mediumStatus": 75 };

export default function ViewStanzeNew() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { selectedDate, selectedStartTime, selectedEndTime, selectedZona } = useSelector(state => state.prenotazioneNew);
    const [percentStatusConfig, setPercentStatusConfig] = useState(defaultPercent);
    const [stanzeList, setStanzeList] = useState([]);
    const [loading, setLoading] = useState(false);
    const isMobile = useMobileDetection();
    const hasLoadedOnce = useRef(false);

    useEffect(() => {
        let isMounted = true;

        const fetchStanze = async () => {
            if (selectedZona?.id) {
                setLoading(true);
                const { method, action } = GetAllElelementsWithPrenotazioniCount;

                const body = {
                    GiornoInizio: moment(selectedDate).format("YYYY-MM-DD"),
                    OraInizio: moment(selectedStartTime).format("HH:mm"),
                    OraFine: moment(selectedEndTime).format("HH:mm"),
                    IdEdificio: null,
                    IdZona: selectedZona.id,
                    IdStanza: null
                };

                try {
                    const response = await axios({
                        method: method,
                        url: urlBase + action,
                        data: body,
                        headers: { "Content-Type": "application/json" },
                    });

                    if (isMounted) {
                        setPercentStatusConfig(response.data.result.percentStatusConfiguration || defaultPercent);
                        setStanzeList(response.data.result.listDeskBookingElement || []);
                        hasLoadedOnce.current = true;
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    if (isMounted) setLoading(false);
                }
            }
        };

        fetchStanze();
        return () => { isMounted = false; };
    }, [selectedZona, selectedDate, selectedStartTime, selectedEndTime]);

    const handleSelectStanza = (stanza) => {
        dispatch(setPrenotaNewStanza(stanza));
    };

    return (
        <Grid container spacing={1} sx={{ height: 'auto' }}>
            {loading && !hasLoadedOnce.current ? (
                <Grid item xs={12}>
                    <InlineLoader message={t("load_rooms")} />
                </Grid>
            ) : stanzeList.length > 0 ? (
                stanzeList.map((stanza) => {
                    const totPostazioni = stanza.totPostazioni || 0;
                    const totOccupate = stanza.totPostazioniOccupate || 0;
                    const minutiTotali = stanza.minutiTotali || 0;
                    const minutiOccupati = stanza.minutiOccupati || 0;
                    const isSalaRiunione = stanza.isSalaRiunioni === true;

                    const { valueProgressBar, colorProgressbar } = CalcPercenteProgressBar(
                        percentStatusConfig,
                        isSalaRiunione ? minutiOccupati : totOccupate,
                        isSalaRiunione ? minutiTotali : totPostazioni
                    );

                    const hasElements = isSalaRiunione ? minutiTotali > 0 : totPostazioni > 0;

                    return (
                        <Grid item xs={12} md={6} key={stanza.id}>
                            <Paper
                                className="rowDisponibilita box-shadow-light"
                                onClick={() => handleSelectStanza(stanza)}
                                sx={{
                                    cursor: 'pointer',
                                    margin: '4px 0',
                                    padding: 0,
                                    overflow: 'hidden',
                                    filter: loading ? 'grayscale(0.3)' : 'none',
                                    transition: 'filter 0.3s ease'
                                }}
                            >
                                <Grid container>
                                    <Grid item xs={2} sm={1.5} md={1}>
                                        <Paper className="cardDisponibilitaSigla flex-center-center no-box-shadow" sx={{ height: '100%', minHeight: '60px' }}>
                                            <Typography sx={{ color: "#3e6bb4", fontWeight: 'bold' }}>
                                                {stanza.sigla?.toUpperCase()}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={8} sm={5} md={6} sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500 }} className="textDisponibilta">
                                                {stanza.denominazione}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" className="textDisponibilta">
                                            {hasElements ? (
                                                isSalaRiunione ? (
                                                    <i>{FormatMinutiToOre(minutiOccupati)}/{FormatMinutiToOre(minutiTotali)}{t("planner_hours")}</i>
                                                ) : (
                                                    <i>{totOccupate}/{totPostazioni}</i>
                                                )
                                            ) : (
                                                <i>{t("no_items")}</i>
                                            )}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2} sm={5.5} md={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 2 }}>
                                        {hasElements && (
                                            isMobile ? (
                                                <ProgressBarDisponibilitaCircular colorProgressBar={colorProgressbar} value={valueProgressBar} />
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <ProgressBarDisponibilitaLinear colorProgressBar={colorProgressbar} value={valueProgressBar} />
                                                </Box>
                                            )
                                        )}
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    );
                })
            ) : (
                !loading && hasLoadedOnce.current && (
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '75vh' }}>
                        <Typography variant="body1">{t("no_rooms_available")}</Typography>
                    </Grid>
                )
            )}
        </Grid>
    );
}